import useCart from 'hooks/account/cart/useCart'
import useTranslations from 'hooks/useTranslations'
import { useCustomTailor, useDisplayVariant } from 'modules/productDetail'
import useCustomFields from 'modules/productDetail/hooks/useCustomFields'
import React from 'react'
import styled from 'styled-components'
import Button from 'theme/atoms/Button'
import { DeliveryInformation } from 'utils/calculateDeliveryDate'

type Props = {
  openFilterDrawer: () => void
  amount: number
  deliveryInformations: DeliveryInformation
  channelInactive: 'b2b' | 'b2c' | null
}

export default function CartButton({
  amount,
  deliveryInformations,
  channelInactive,
  openFilterDrawer
}: Props) {
  const variant = useDisplayVariant()
  const t = useTranslations<'templates-PDP'>()
  const cart = useCart()
  const customFields = useCustomFields()
  const customTailor = useCustomTailor()

  const isCustomTailor =
    variant.data?.configurations.custom_tailor.is_custom_tailor || false

  // TODO: custom tailor changed and created merge conflict so we discarded change
  const addToCart = () => {
    if (!cart.updating) {
      if (!isCustomTailor) {
        const cartItemIndex = cart.data.items.findIndex(
          (item) => item.sw6Uuid === variant.data.sw6Uuid
        )
        if (cartItemIndex === -1) {
          cart.addItem(variant.data.sw6Uuid, amount, {
            item_list_name: customFields.data?.itemListName || '',
            list_position: customFields.data?.productPosition || 0,
            item_list_id: customFields.data?.itemListId || ''
          })
        } else {
          cart.updateItem(
            cart.data.items[cartItemIndex].cartItemId,
            amount + cart.data.items[cartItemIndex].quantity
          )
        }
      } else {
        if (
          customTailor.data.shape &&
          customTailor.data.size.width &&
          customTailor.data.brink
        ) {
          cart.addItem(
            variant.data.sw6Uuid,
            amount,
            {
              item_list_name: customFields.data?.itemListName || '',
              list_position: customFields.data?.productPosition || 0,
              item_list_id: customFields.data?.itemListId || ''
            },
            {
              customProductData: {
                form: customTailor.data.shape,
                squareM: `${customTailor.getSquareM()}`,
                size: {
                  width: `${customTailor.data.size?.width || ''}`,
                  length: `${
                    customTailor.data.size?.length ||
                    customTailor.data.size?.width ||
                    ''
                  }`
                },
                brink: {
                  type: customTailor.data.brink?.type || '',
                  width: `${customTailor.data.brink?.width || ''}`
                },
                priceTotal: {
                  net: `${customTailor.getSinglePrice(
                    variant.data.prices.packPriceNet,
                    variant
                  )}`,
                  gross: `${customTailor.getSinglePrice(
                    variant.data.prices.packPriceGross,
                    variant
                  )}`
                }
              }
            }
          )
        }
      }
    }
  }

  // display channelRestriction (can not add to Cart) button if product is not sellable for current channel
  if (channelInactive) {
    return (
      <div className="buy-wrapper" data-cy-state="channel-restriction-b2x">
        <Button variation="cart_channel_restriction" $fullWidth icon="disabled">
          {t(`only_for_${channelInactive}`)}
        </Button>
      </div>
    )
  }

  // display sold out button if product is not sellable
  if (!variant.data.sellable || !deliveryInformations.isBuyable) {
    return (
      <Wrapper className="CartButton" data-cy-state="show-sold-out-btn">
        <Button
          variation="cart_disabled_pdp"
          className="sold-out-button"
          $fullWidth
        >
          {t('sold_out_btn')}
        </Button>
      </Wrapper>
    )
  }

  // displays customize button if product is customizable and at least one configuration is not set
  if (
    isCustomTailor &&
    !(
      // if one configuration is not set, cart button will be disabled
      (
        customTailor.data.shape &&
        customTailor.data.size.width &&
        customTailor.data.brink &&
        (customTailor.data?.shape === 'rectangular' ||
        customTailor.data?.shape === 'oval'
          ? customTailor.data.size.length
          : true)
      )
    )
  ) {
    return (
      <Wrapper className="CartButton" data-cy-state="show-customize-btn">
        <Button
          variation="cart_disabled"
          data-cy-handle="customize-btn"
          className="customize-button"
          $fullWidth
          onClick={openFilterDrawer}
        >
          {t('please_customize')}
        </Button>
      </Wrapper>
    )
  }

  return (
    <Wrapper className="CartButton">
      <Button
        variation={cart.setup ? 'cart' : 'cart_disabled_pdp'}
        data-cy-handle="add-to-cart-btn"
        onClick={addToCart}
        $fullWidth
        submitable
        disabled={!cart.setup}
        icon="cart"
        isSubmitting={cart.updating}
        isSubmittingState="adding-to-cart"
      >
        {t('add_to_cart_btn')}
      </Button>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  flex: 1;
`
